import { Environment, InputFile, Nf, Tag, onEnvironmentChange } from '@hub/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './card-xml-data.component.scoped.scss'
import './card-xml-data.component.scss'

// 10MB
const MAXIMUM_SIZE_IN_BYTES = 10485760

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  nfData?: Nf
  transactionState: string
  onChange: (value: any) => any
  onValidate: (value: boolean) => any
  isReplaceInvoice?: boolean
}

export default ({ nfData, transactionState, onChange, onValidate, isReplaceInvoice }: Props) => {
  const onEnvironmentChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [urlCreators, setUrlCreators] = useState<string>('')
  const { t } = useTranslation('cardXmlData')
  const [state, setState] = useState<any>({ file: '', error: '' })
  const statusXml = useMemo(() => (nfData?.xmlUrlStorage ? 'alreadySend' : null), [nfData])

  const statusXmlObject = useMemo(
    () => ({
      alreadySend: {
        icon: 'fa-solid fa-circle-check',
        colorIcon: 'var(--green)',
        label: t('documentSent'),
      },
    }),
    [t]
  )

  useEffect(() => {
    onEnvironmentChangeSubscription.current = onEnvironmentChange.subscribe((environments: Environment) => {
      setUrlCreators(environments?.squidProducts?.creators)
    })
    return () => onEnvironmentChangeSubscription?.current?.unsubscribe()
  }, [])

  useEffect(() => {
    if (onChange && typeof onChange === 'function') {
      onChange(state)
    }
  }, [state, onChange])

  const handleChange = useCallback(
    (field: string, value: any) => {
      if (value.length && value[0]?.type !== 'text/xml') {
        state.error = t('invalidFile')
      } else {
        state.error = ''
      }

      setState({ ...state, [field]: value, error: state.error })

      if (onValidate && typeof onValidate === 'function') {
        onValidate(!state.error)
      }
      if (onChange && typeof onChange === 'function') {
        onChange(state.value)
      }
    },
    [onChange, onValidate, state, t]
  )

  return (
    <div className="card-xml-data display-flex-column justify-content-space-between'">
      <div>
        <h5 className="title m-0">XML</h5>
        <p
          className="description"
          dangerouslySetInnerHTML={{ __html: t('description', { url: `${urlCreators}/docs/o-que-e-xml?highlight=xml` }) }}
        />
      </div>
      <div className="display-flex justify-content-center" style={{ gap: '1rem', width: isReplaceInvoice ? '100%' : 'auto' }}>
        {statusXml && !isReplaceInvoice && (
          <a href={nfData?.xmlUrlStorage} target="_blank" className="view-nf button button-primary small" id="view-nf">
            {t('downloadXML')}
          </a>
        )}
        <InputFile
          name="input-xml-file"
          id="input-xml-file"
          className="input-xml-file"
          placeholder={statusXml ? t('replaceXML') : t('selectXML')}
          required
          onChange={(value) => handleChange('file', value)}
          maxFileSize={MAXIMUM_SIZE_IN_BYTES}
          accept="text/xml"
          externalError={state?.error}
          errorSpan={isReplaceInvoice}
          showFileName={state?.file}
          buttonSize="md"
          disabled={!['pending', 'review'].includes(transactionState)}
          buttonStyle={{
            display: 'flex',
            alignItens: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--transparent)',
            color: 'var(--primary_color)',
          }}
        ></InputFile>
      </div>
      {statusXml && (
        <Tag
          labels={[
            <i className={statusXmlObject?.[statusXml]?.icon} style={{ color: `${statusXmlObject?.[statusXml]?.colorIcon}` }}></i>,
            <span>
              Status: <strong>{statusXmlObject?.[statusXml]?.label}</strong>
            </span>,
          ]}
          style={{ width: '100%', border: 'none', backgroundColor: 'var(--background)' }}
        />
      )}
    </div>
  )
}
